/**
 * useCalculation Hook
 *
 * Triggers BOM calculations and manages calculation state.
 */

import { useCallback, useEffect } from 'react';
import { useStore } from '../store/store';
import { calculateBOM } from '../services/bomCalculator';

export function useCalculation() {
  const configuration = useStore((state) => state.configuration);
  const bom = useStore((state) => state.bom);
  const isCalculating = useStore((state) => state.isCalculating);
  const error = useStore((state) => state.calculationError);

  const setBOM = useStore((state) => state.setBOM);
  const setIsCalculating = useStore((state) => state.setIsCalculating);
  const setCalculationError = useStore((state) => state.setCalculationError);
  const updateComputedValues = useStore((state) => state.updateComputedValues);

  const calculate = useCallback(() => {
    try {
      setIsCalculating(true);
      setCalculationError(null);

      // Perform calculation (bomCalculator accepts UI config directly)
      const result = calculateBOM(configuration);
      console.log('[useCalculation] BOM calculated:', {
        hasResult: !!result,
        hasSummary: !!result?.summary,
        nodeCount: result?.summary?.compute?.totalNodes,
      });

      // Update store
      setBOM(result);
      console.log('[useCalculation] setBOM called with result');

      // Verify BOM was set
      const currentBOM = useStore.getState().bom;
      console.log('[useCalculation] BOM in store after setBOM:', {
        hasBOM: !!currentBOM,
        nodeCount: currentBOM?.summary?.compute?.totalNodes,
      });

      // Update computed values
      updateComputedValues({
        nodes: result.summary.compute.totalNodes,
        racks: result.summary.space.totalRacks,
        powerKw: result.summary.power.totalFacilityPowerKw,
        switchCount: result.summary.network.totalSwitches,
        cables: result.summary.network.totalCables,
      });

      setIsCalculating(false);
    } catch (err) {
      console.error('[useCalculation] Calculation error:', err);
      setCalculationError(err instanceof Error ? err.message : 'Calculation failed');
      setIsCalculating(false);
    }
  }, [configuration, setBOM, setIsCalculating, setCalculationError, updateComputedValues]);

  // Auto-calculate on configuration changes
  useEffect(() => {
    const timer = setTimeout(() => {
      calculate();
    }, 300); // Debounce

    return () => clearTimeout(timer);
  }, [configuration.scaling, configuration.network, configuration.rack, configuration.power]);

  return {
    bom,
    calculate,
    isCalculating,
    error,
  };
}
